/**
 * Motion — shared animation layer for the public marketing site.
 *
 * <MotionStyles/> injects all keyframes + reveal/hover CSS once.
 * useScrollReveal() attaches an IntersectionObserver that adds `.in` to any
 * [data-reveal] element as it scrolls into view (staggered via --d delay).
 * useCountUp() animates numeric metrics from 0 to their value on reveal.
 *
 * Respects prefers-reduced-motion: everything degrades to instant/visible.
 */
import { useEffect } from 'react';

export function MotionStyles() {
  return (
    <style>{`
      @keyframes hhmsFadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:none; } }
      @keyframes hhmsFadeIn { from { opacity:0; } to { opacity:1; } }
      @keyframes hhmsFloat  { 0%,100% { transform:translateY(0) translateX(0); } 50% { transform:translateY(-26px) translateX(14px); } }
      @keyframes hhmsFloat2 { 0%,100% { transform:translateY(0) translateX(0); } 50% { transform:translateY(22px) translateX(-18px); } }
      @keyframes hhmsGradient { 0% { background-position:0% 50%; } 50% { background-position:100% 50%; } 100% { background-position:0% 50%; } }
      @keyframes hhmsMarquee { from { transform:translateX(0); } to { transform:translateX(-50%); } }
      @keyframes hhmsPulseRing { 0% { box-shadow:0 0 0 0 rgba(79,195,247,.45);} 70%{ box-shadow:0 0 0 14px rgba(79,195,247,0);} 100%{ box-shadow:0 0 0 0 rgba(79,195,247,0);} }

      /* scroll reveal */
      [data-reveal]{ opacity:0; transform:translateY(28px); transition:opacity .7s cubic-bezier(.16,.84,.44,1), transform .7s cubic-bezier(.16,.84,.44,1); transition-delay:var(--d,0ms); will-change:opacity,transform; }
      [data-reveal].in{ opacity:1; transform:none; }

      /* hero entrance (no observer needed — above the fold) */
      .hhms-anim-up{ animation:hhmsFadeUp .8s cubic-bezier(.16,.84,.44,1) both; animation-delay:var(--d,0ms); }

      /* card hover lift */
      .hhms-lift{ transition:transform .28s cubic-bezier(.16,.84,.44,1), box-shadow .28s, border-color .28s; will-change:transform; }
      .hhms-lift:hover{ transform:translateY(-6px); box-shadow:0 18px 40px -18px rgba(13,27,46,.35); }

      /* CTA button shine */
      .hhms-cta-shine{ position:relative; overflow:hidden; }
      .hhms-cta-shine::after{ content:''; position:absolute; top:0; left:-120%; width:60%; height:100%; background:linear-gradient(120deg,transparent,rgba(255,255,255,.35),transparent); transform:skewX(-20deg); transition:left .6s; }
      .hhms-cta-shine:hover::after{ left:140%; }

      @media (prefers-reduced-motion: reduce){
        [data-reveal]{ opacity:1 !important; transform:none !important; transition:none !important; }
        .hhms-anim-up{ animation:none !important; }
        .hhms-float, .hhms-float2{ animation:none !important; }
        .hhms-lift:hover{ transform:none !important; }
      }
    `}</style>
  );
}

/** Reveal [data-reveal] elements on scroll. Re-scans on route/content change. */
export function useScrollReveal(deps = []) {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const els = Array.from(document.querySelectorAll('[data-reveal]:not(.in)'));
    if (!els.length) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    // safety: anything already in view on mount
    requestAnimationFrame(() => els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92) el.classList.add('in');
    }));
    return () => io.disconnect();
  }, deps);
}

/** Count numeric metrics up from 0 when they enter view. */
export function useCountUp(deps = []) {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const els = Array.from(document.querySelectorAll('[data-countup]:not([data-done])'));
    if (!els.length) return undefined;
    const animate = (el) => {
      const raw = el.getAttribute('data-countup');
      const m = String(raw).match(/([\d,]+(?:\.\d+)?)/);
      if (!m) { el.setAttribute('data-done', '1'); return; }
      const target = parseFloat(m[1].replace(/,/g, ''));
      const prefix = raw.slice(0, m.index);
      const suffix = raw.slice(m.index + m[1].length);
      const hasComma = m[1].includes(',');
      const fmt = (n) => {
        const v = Number.isInteger(target) ? Math.round(n) : n.toFixed(1);
        return prefix + (hasComma ? Number(v).toLocaleString() : v) + suffix;
      };
      if (reduce) { el.textContent = fmt(target); el.setAttribute('data-done', '1'); return; }
      const dur = 1400; const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt(target * eased);
        if (p < 1) requestAnimationFrame(tick); else el.setAttribute('data-done', '1');
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { animate(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, deps);
}
